/** @format */

import { ReloadOutlined } from "@ant-design/icons";
import { Button, Skeleton } from "antd";
import moment from "moment";
import React from "react";
import { Collegamento } from "./Collegamento";
import { THomeDaGestire } from "../../../../redux-modules/declarations/maps/home";
import { Routes } from "../../../../utils/Routes";
import { TPeriodo } from "../../ViewPresenze/partials/DaGestire";
import { TLettura } from "../../ViewPresenze/usePresenze";
import { etichettaPeriodo, giornoBreveRelativo, plurale } from "../format";

interface Props {
  lettura: TLettura<THomeDaGestire>;
  periodo: TPeriodo;
  oggi: string;
  /** vista settimanale dei turni attiva per la tenance, altrimenti griglia giornaliera */
  turniSettimanali: boolean;
}

interface TVoce {
  chiave: string;
  n: number;
  colore: "blu" | "rosso" | "ambra" | "grigio";
  testo: string;
  dettaglio: string | null;
  titolo: string | null;
  a: string;
}

/**
 * Cosa c'è da gestire per un responsabile, ognuno con il suo collegamento. Le voci arrivano solo
 * con il permesso che serve per gestirle; anomalie e banca ore sono gli stessi numeri della scheda
 * "Da gestire" di Presenze, sullo stesso periodo.
 */
export const RigaDaGestire = ({
  lettura,
  periodo,
  oggi,
  turniSettimanali,
}: Props): JSX.Element | null => {
  const { dati, caricamento, errore, ricarica } = lettura;

  if (caricamento && !dati) {
    return (
      <div className="da-gestire" aria-busy="true">
        <span className="etichetta">Da gestire</span>
        <Skeleton.Button active size="small" style={{ width: 180, borderRadius: 18 }} />
        <Skeleton.Button active size="small" style={{ width: 180, borderRadius: 18 }} />
      </div>
    );
  }
  if (errore && !dati) {
    return (
      <div className="da-gestire">
        <span className="etichetta">Da gestire</span>
        <span className="attenuato">{errore}</span>
        <Button size="small" icon={<ReloadOutlined />} onClick={ricarica}>
          Riprova
        </Button>
      </div>
    );
  }
  if (!dati) return null;

  const presenzeDaGestire = `${Routes.presences}?vista=da-gestire`;
  const voci: TVoce[] = [];

  if (dati.richieste) {
    voci.push({
      chiave: "richieste",
      n: dati.richieste.in_attesa,
      colore: "blu",
      testo: dati.richieste.in_attesa === 1 ? "Richiesta da approvare" : "Richieste da approvare",
      dettaglio: null,
      titolo: dati.richieste.per_tipo.map(t => `${t.n} ${t.tipo}`).join(", ") || null,
      a: Routes.requests,
    });
  }
  if (dati.anomalie) {
    const periodoTesto = etichettaPeriodo(periodo.tipo);
    voci.push({
      chiave: "anomalie",
      n: dati.anomalie.anomalie,
      colore: "rosso",
      testo: dati.anomalie.anomalie === 1 ? "Anomalia" : "Anomalie",
      dettaglio: null,
      titolo:
        dati.anomalie.persone > 0
          ? `${plurale(dati.anomalie.persone, "persona", "persone")} con anomalie, ${periodoTesto}`
          : null,
      a: presenzeDaGestire,
    });
    if (dati.anomalie.banca_ore !== null) {
      voci.push({
        chiave: "banca-ore",
        n: dati.anomalie.banca_ore,
        colore: "ambra",
        testo: "Banca ore da approvare",
        dettaglio: null,
        titolo: null,
        a: presenzeDaGestire,
      });
    }
  }
  if (dati.cambi_turno) {
    const primo = dati.cambi_turno.primo;
    voci.push({
      chiave: "cambi",
      n: dati.cambi_turno.n,
      colore: "grigio",
      testo: dati.cambi_turno.n === 1 ? "Cambio turno richiesto" : "Cambi turno richiesti",
      dettaglio: primo ? `${primo.persona}, ${giornoBreveRelativo(primo.data, oggi)}` : null,
      titolo: primo
        ? `Il primo: ${primo.persona}, ${moment(primo.data).format("dddd D MMMM")}`
        : null,
      a: turniSettimanali ? Routes.shifts : Routes.shiftsDailyGrid,
    });
  }

  if (!voci.length) return null;

  return (
    <nav className="da-gestire" aria-label="Da gestire">
      <span className="etichetta">
        Da gestire
        {dati.anomalie && (
          <span className="periodo"> · anomalie {etichettaPeriodo(periodo.tipo)}</span>
        )}
      </span>
      {voci.map(v => (
        <Collegamento
          key={v.chiave}
          to={v.a}
          className={`voce${v.n === 0 ? " zero" : ""}`}
          title={v.titolo || undefined}
        >
          <span className={`n numeri ${v.n === 0 ? "grigio" : v.colore}`}>{v.n}</span>
          {v.testo}
          {v.dettaglio && v.n > 0 && <span className="periodo">· {v.dettaglio}</span>}
        </Collegamento>
      ))}
    </nav>
  );
};
